import React from 'react'

import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Redirect, Route, Switch } from 'react-router-dom'

import { authorized } from 'common/authorizer'
import collegeFeatures from 'common/college-features'
import permissions from 'common/system-permissions'
import { isFeatureEnabled } from 'store/college'
import { isHandraisersEnabled } from 'store/feature-toggle'
import { hasPermission } from 'store/user'
import { useCanViewApplicants } from 'store/user/hooks'

import rememberedRedirectHelper from './remembered-redirect-helper'
import InAppLayout from './routes/InAppLayout'
import ActivateApplication from './routes/application/Activate'
import AppCycleRounds from './routes/application/AppCycleRounds'
import ApplicantApplicationsRoute from './routes/application/Applicants'
import ConfigureApplication from './routes/application/Configure'
import ApplicationExtensionsRoute from './routes/application/Extensions'
import CollegeDetailsRoute from './routes/college'
import ConfigureCrmIntegration from './routes/configure-integration'
import ContentManagement from './routes/content-management'
import Dashboard from './routes/dashboard/Dashboard'
import DocumentsRoute from './routes/documents'
import HandraisersRoute from './routes/handraisers'
import HighSchoolsRoutes from './routes/highschools'
import ManageVisitsTable from './routes/highschools/ManageVisits'
import MessageCenter from './routes/message-center/MessageCenterV2'
import ManageUsers from './routes/org/ManageUsers'
import Outreach from './routes/outreach'
import CollegeSearchRoute from './routes/search'
import Settings from './routes/settings'
import FourOhFourRoute from './routes/status/FourOhFour'
import Visits from './routes/visits/Visits'

const withManagementAuthorization = authorized([permissions.MANAGE_ADMISSIONS])
const withVisitsAuthorization = authorized([permissions.PERFORM_VISIT_MANGEMENT_TASKS])
const WrappedManageUsersRoute = withManagementAuthorization(ManageUsers)
const WrappedVisitsRoute = withVisitsAuthorization(Visits)
const WrappedMessageCenterRoute = withVisitsAuthorization(MessageCenter)

const WrappedHighSchoolsRoutes = withVisitsAuthorization(HighSchoolsRoutes)

export const AuthRoutes = ({
  canManageContent = false,
  canManageOutreach = false,
  supportsContentManagement = false,
  supportsLimitedAutomatedMessages = false,
  supportsStudentOutreach = false,
  supportsCrmIntegration = false,
  handraisersFeatureEnabled = false,
  supportsHandraisers = false,
  canViewHandraisers = false,
  canViewDocuments = false,
  scid,
  ...rest
}) => {
  const ROOT_PATH = import.meta.env.BASE_URL
  const canViewApplicants = useCanViewApplicants()
  if (rememberedRedirectHelper.hasRememberedRoute()) {
    rememberedRedirectHelper.handleRemembered()
    return null
  }
  return (
    <InAppLayout {...rest}>
      <Switch>
        <Redirect
          from={`${ROOT_PATH}/home`}
          to={`${ROOT_PATH}/dashboard`}
        />
        <Redirect
          exact
          from={`${ROOT_PATH}/message-center`}
          to={`${ROOT_PATH}/message-center/messages`}
        />
        {/* Reroute deprecated paths */}
        <Redirect
          from={`${ROOT_PATH}/recruit/explore`}
          to={`${ROOT_PATH}/recruit/highschools`}
        />
        <Redirect
          exact
          from={`${ROOT_PATH}/recruit`}
          to={`${ROOT_PATH}/recruit/highschools`}
        />
        <Redirect
          exact
          from={`${ROOT_PATH}/colleges`}
          to={`${ROOT_PATH}/colleges/${scid}`}
        />
        <Redirect
          exact
          from={`${ROOT_PATH}/engage`}
          to={`${ROOT_PATH}/engage/presence`}
        />
        <Redirect
          from={`${ROOT_PATH}/integrations`}
          to={`${ROOT_PATH}/documents/integrations`}
        />
        <Redirect
          from={`${ROOT_PATH}/engage/content-management`}
          to={`${ROOT_PATH}/engage/presence`}
        />
        <Redirect
          from={`${ROOT_PATH}/content-management`}
          to={`${ROOT_PATH}/engage/presence`}
        />
        <Redirect
          from={`${ROOT_PATH}/outreach`}
          to={`${ROOT_PATH}/engage/messaging`}
        />
        <Redirect
          from={`${ROOT_PATH}/engage/outreach`}
          to={`${ROOT_PATH}/engage/messaging`}
        />
        <Route
          path={`${ROOT_PATH}/dashboard`}
          component={Dashboard}
        />
        <Redirect
          exact
          from={`${ROOT_PATH}/application`}
          to={`${ROOT_PATH}/application/${canViewApplicants ? 'applicants' : 'rounds'}`}
        />
        <Route
          exact
          path={`${ROOT_PATH}/application/applicants`}
          component={ApplicantApplicationsRoute}
        />
        <Route
          exact
          path={`${ROOT_PATH}/application/rounds`}
          component={AppCycleRounds}
        />
        <Route
          exact
          path={`${ROOT_PATH}/application/extensions`}
          component={ApplicationExtensionsRoute}
        />
        <Route
          path={`${ROOT_PATH}/application/settings`}
          component={ConfigureApplication}
        />
        <Route
          path={`${ROOT_PATH}/application/integration`}
          component={ActivateApplication}
        />
        <Route
          path={`${ROOT_PATH}/colleges/search`}
          component={CollegeSearchRoute}
        />
        <Route
          path={`${ROOT_PATH}/colleges/:scid/:section?`}
          render={(props) => {
            const { match } = props
            const { params } = match
            const { scid } = params
            return (
              <CollegeDetailsRoute
                {...props}
                url={match.url}
                section={params.section}
                scid={parseInt(scid, 10)}
              />
            )
          }}
        />
        <Route
          path={`${ROOT_PATH}/documents`}
          render={(props) => (
            <DocumentsRoute
              {...props}
              canViewDocuments={canViewDocuments}
            />
          )}
        />
        <Route
          exact
          path={`${ROOT_PATH}/org/users`}
          render={(props) => <WrappedManageUsersRoute {...props} />}
        />
        <Redirect
          exact
          from={`${ROOT_PATH}/settings`}
          to={`${ROOT_PATH}/settings/profile`}
        />
        <Route
          path={`${ROOT_PATH}/settings`}
          render={(props) => <Settings {...props} />}
        />
        <Route
          path={`${ROOT_PATH}/recruit/visits`}
          render={(props) => <WrappedVisitsRoute {...props} />}
        />
        <Route
          path={`${ROOT_PATH}/recruit/manage-visits`}
          render={(props) => <ManageVisitsTable {...props} />}
        />
        <Route
          path={`${ROOT_PATH}/recruit/highschools`}
          render={(props) => <WrappedHighSchoolsRoutes {...props} />}
        />
        <Route
          path={`${ROOT_PATH}/message-center`}
          render={(props) => <WrappedMessageCenterRoute {...props} />}
        />
        <Route
          path={`${ROOT_PATH}/engage/presence`}
          render={(props) => (
            <ContentManagement
              {...props}
              canManageContent={canManageContent}
              supportsContentManagement={supportsContentManagement}
            />
          )}
        />
        {supportsCrmIntegration && (
          <Route
            path={`${ROOT_PATH}/engage/integrations`}
            component={ConfigureCrmIntegration}
          />
        )}
        {supportsCrmIntegration && (
          <Redirect
            exact
            from={`${ROOT_PATH}/engage`}
            to={`${ROOT_PATH}/engage/integrations`}
          />
        )}
        <Route
          path={`${ROOT_PATH}/engage/messaging`}
          render={(props) => (
            <Outreach
              {...props}
              canManageOutreach={canManageOutreach}
              supportsStudentOutreach={supportsStudentOutreach}
              supportsLimitedAutomatedMessages={supportsLimitedAutomatedMessages}
            />
          )}
        />
        {handraisersFeatureEnabled && (
          <Route
            path={`${ROOT_PATH}/handraisers`}
            render={(props) => (
              <HandraisersRoute
                {...props}
                canViewHandraisers={canViewHandraisers}
                supportsHandraisers={supportsHandraisers}
              />
            )}
          />
        )}
        <FourOhFourRoute />
      </Switch>
    </InAppLayout>
  )
}

AuthRoutes.propTypes = {
  scid: PropTypes.number.isRequired,
  canManageContent: PropTypes.bool.isRequired,
  canManageOutreach: PropTypes.bool.isRequired,
  supportsEndOfCycleReport: PropTypes.bool,
  supportsCrmIntegration: PropTypes.bool,
}

export default connect((state) => ({
  scid: state.college?.record?.Scid,
  supportsContentManagement: isFeatureEnabled(state, collegeFeatures.SUPPORTS_CONTENT_MANAGEMENT),
  canManageContent: hasPermission(state, permissions.MANAGE_COLLEGE_CONTENT),
  supportsLimitedAutomatedMessages: isFeatureEnabled(state, collegeFeatures.SUPPORTS_LIMITED_AUTOMATED_MESSAGES),
  supportsStudentOutreach: isFeatureEnabled(state, collegeFeatures.SUPPORTS_STUDENT_OUTREACH),
  canManageOutreach: hasPermission(state, permissions.MANAGE_COLLEGE_OUTREACH_MESSAGES),
  supportsCrmIntegration: isFeatureEnabled(state, collegeFeatures.CRM_INTEGRATION),
  supportsHandraisers: isFeatureEnabled(state, collegeFeatures.SUPPORTS_HANDRAISERS),
  handraisersFeatureEnabled: isHandraisersEnabled(state),
  canViewHandraisers: hasPermission(state, permissions.VIEW_STUDENT_INTERESTS),
  canViewDocuments: hasPermission(state, permissions.PERFORM_ADMISSIONS_TASKS),
}))(AuthRoutes)
